import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTenancy } from '@/contexts/TenancyContext';
import { useMSGReadOnly } from '../../contexts/ReadOnlyContext';
import { db, firebaseImport } from '@/js/external-js/config/main_config';

import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Toggle } from "@/components/ui/toggle"

import EditableField from '@/components/ui/EditableField';
import { FrameworkBadge } from '@/app/pages/msg/components/FrameworkBadge';

import { sanitise } from '@/js/external-js/variableModules/utils/sanitise.util';
import { Edit, Eye, Plus, Trash2 } from 'lucide-react';

import {
  getStandardName,
  fetchRelatedDocuments,
  populateStandardsList,
  populateCustomContent,
  fetchPageContent,
  fetchPageContentMDX,
  fetchIssueRegisterItems,
  renderArray,
  getAllModules
} from '@/app/pages/msg/utils/common';

import { MarkdownRenderer } from '@/app/pages/msg/components/MarkdownRenderer';
import { useMarkdownComponents } from '@/app/pages/msg/components/MDXFields';
import RelatedClausesAccordianItem from '@/app/pages/msg/components/RelatedClauses';
import InternalExternalIssuesDialog from '@/app/pages/msg/sections/Context/dialogs/InternalExternalIssues';
import AdditionalContent from '../../components/AdditionalContent';
import FootNote from '../../components/FootNote';
import SupportingDocs from '../../components/SupportingDocs';
import { useMSGVersion } from '../../hooks/useMSGVersion';
import SupportingDocsDropdown from '../../components/SupportingDocsDropdown';

export function cleanStandardName(standard: string) {
  if (!standard) return '';

  standard = standard.toLowerCase();
  const standardMap = {
    disp: "DISP",
    essential8: "Essential 8",
    privacyact: "Privacy Act (PA)",
    soc2: "SOC 2",
    nist80053rev5: "NIST 800-53 Rev 5",
    nist80053: "NIST 800-53",
    nistcsf: "NIST CSF",
    nistcsfrev2: "NIST CSF Rev 2",
    rffr: "RFFR",
    dispcontrols: "DISP",
    e8controls: "Essential 8",
    "iso 27701": "ISO 27701",
    iso27701: "ISO 27701",
    iso270012022annexacontrols: "ISO 27001:2022",
    iso270012022controls: "ISO 27001:2022",
    iso27001: "ISO 27001:2022",
    iso420012023controls: "ISO 42001",
    "nist800-53-rev5": "NIST 800-53 Rev 5",
    pacontrols: "Privacy Act (PA)",
    soc2controls: "SOC 2",
    ism: "ISM",
    smb1001controls: "SMB 1001",
    smb1001: "SMB 1001",
    "qgov-is18": "QGOV IS18",
    iso42001: "ISO 42001",
    iso420012023: "ISO 42001",
    iso9001: "ISO 9001",
    iso45001: "ISO 45001",
    iso14001: "ISO 14001",
  };

  return standardMap[standard] || standard;
}



export default function InternalAndExternalIssuesSection() {
  const { activeTenancy, userIdToName } = useTenancy();
  const ACME_NAME_REGEX = /\bacme\s*,?\s*inc\.?\b/i;
  const sortKey = (val?: string) => {
    const companyDisplay = (activeTenancy?.name || activeTenancy?.companyName || 'Company Name').trim();
    const src = (val || '').trim();
    const replaced = src.replace(ACME_NAME_REGEX, companyDisplay);
    return replaced.toLocaleLowerCase();
  };
  const byDisplayName = (a: any, b: any) => sortKey(a?.name).localeCompare(sortKey(b?.name), undefined, { sensitivity: 'base' });
  const { incrementVersion } = useMSGVersion();
  const { isReadOnly } = useMSGReadOnly();

  // MDX Things
  const [pageTemplateMDX, setPageTemplateMDX] = React.useState<string>('');
  const [modulesList, setModulesList] = useState([]);
  const clauseStandards = useRef<Set<string>>(new Set());
  const [pageClauses, setPageClauses] = useState<Set<string>>(new Set());

  // Function to update pageClauses
  const updatePageClauses = (newClauses: Set<string>) => {
    setPageClauses(new Set(newClauses));
  };

  const [supportingDocs, setSupportingDocs] = React.useState([]);
  const [sortedFrameworks, setSortedFrameworks] = React.useState([]);
  const [dynamicStandardNames, setDynamicStandardNames] = useState<Map<string, string>>(new Map());
  const [dynamicContent, setDynamicContent] = React.useState<string>("");

  const pageName = 'context';
  const contentPath = ['internalAndExternalIssues'];
  const pageRoute = '/MSG/context/internalAndExternalIssues';

  const [issueRegisterItems, setIssueRegisterItems] = React.useState<Array>([]);
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedEditIssue, setSelectedEditIssue] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteIssueId, setDeleteIssueId] = useState<string | null>(null);
  const [deleteIssueOrigin, setDeleteIssueOrigin] = useState<string | null>(null);

  const toggleRow = (id: number) => {
    setExpandedRows(prev =>
      prev.includes(id) ? prev.filter(rowId => rowId !== id) : [...prev, id]
    );
  };

  useEffect(() => {
    if (activeTenancy.id == "" || activeTenancy.id === "Loading...") return;

    const loadModules = async () => {
      const allModules = await getAllModules();
      const storedVisibilities = JSON.parse(localStorage.getItem('modulesVisibility') || '{}');

      allModules.forEach(module => {
        module.visible = storedVisibilities[module.id] ?? false;
      });

      setModulesList(allModules);
    }

    const getPageContentMDX = fetchPageContentMDX(db, pageName, 'internalAndExternalIssues_MDX', activeTenancy, setPageTemplateMDX);

    // const getRelatedDocuments = fetchRelatedDocuments(db, activeTenancy.id, pageRoute, setSupportingDocs);

    // From src/components/relatedControls.component:195
    // const popStandardsList = populateStandardsList(db, pageName, contentPath, setDynamicStandardNames, setSortedFrameworks);

    // New function to populate the contents from the customer
    const getCustomContent = populateCustomContent(db, activeTenancy.id, pageRoute, setDynamicContent);


    const load = async () => {
      // await popStandardsList();
      // await getIssueRegisterItems();
      await fetchIssueRegisterItems(db, activeTenancy.id, setIssueRegisterItems);
    }; load();
    loadModules();
    return () => {
      getPageContentMDX();
      getCustomContent();
      // getRelatedDocuments();
    };
  }, [activeTenancy]);

  const onActions = {
    onEdit: (row: any) => {
      console.log('[MSG/InternalExternalIssues:onEdit]', row);
      setSelectedEditIssue(row);
      setDialogOpen(true);
    },
  };

  // Deep-link: open issue edit when /msg/context/issues/:id
  const { id: routeIssueId } = useParams<{ id?: string }>();
  useEffect(() => {
    const rid = String(routeIssueId || '').trim();
    if (!rid || !Array.isArray(issueRegisterItems) || issueRegisterItems.length === 0) return;
    const match = issueRegisterItems.find(i => String(i.id) === rid);
    if (match) {
      setSelectedEditIssue(match);
      setDialogOpen(true);
    }
  }, [routeIssueId, issueRegisterItems]);

  const handleSave = async (updatedIssue: any) => {
    console.log('[MSG/InternalExternalIssues:handleSave]', updatedIssue);
    const isNew = !updatedIssue.id || updatedIssue.id.startsWith('temp-');
    const oldIssue = isNew ? null : issueRegisterItems.find(i => i.id === updatedIssue.id);

    try {
      // First, get the current items to work with
      const currentItems = [...issueRegisterItems];
      let issueToSave = { ...updatedIssue };

      // Ensure we have all required fields
      if (!issueToSave.id) {
        // New issue
        issueToSave = {
          ...issueToSave,
          id: `ISU-${Date.now()}`,
          dateAdded: new Date().toISOString().split('T')[0],
          details: {
            issueType: issueToSave.details?.issueType || '',
            risks: issueToSave.details?.risks || [],
            objectives: issueToSave.details?.objectives || [],
            legalObligations: issueToSave.details?.legalObligations || [],
            frameworks: issueToSave.details?.frameworks || []
          }
        };

        // Add to local state
        setIssueRegisterItems(prev => [...prev, issueToSave]);
      } else {
        // Existing issue - find and update
        const existingIndex = currentItems.findIndex(item => item.id === updatedIssue.id);
        if (existingIndex >= 0) {
          const existingItem = currentItems[existingIndex];
          issueToSave = {
            ...existingItem, // Preserve all existing fields
            ...updatedIssue, // Apply updates
            details: {
              ...(existingItem.details || {}), // Preserve existing details
              ...(updatedIssue.details || {}), // Apply detail updates
              // Ensure arrays exist
              risks: updatedIssue.details?.risks || [],
              objectives: updatedIssue.details?.objectives || [],
              legalObligations: updatedIssue.details?.legalObligations || [],
              frameworks: updatedIssue.details?.frameworks || []
            },
            // Preserve original dateAdded
            dateAdded: existingItem.dateAdded || new Date().toISOString().split('T')[0]
          };

          // Update local state
          const updatedItems = [...currentItems];
          updatedItems[existingIndex] = issueToSave;
          setIssueRegisterItems(updatedItems);
        }
      }

      // Save to database
      const issueRef = db.collection('customers')
        .doc(activeTenancy.id)
        .collection('product')
        .doc('managementSystemGuide')
        .collection('issueRegister')
        .doc('register');

      if (!issueToSave.id) {
        issueToSave.id = `ISU-${Date.now()}`;
      }

      console.log('[InternalExternalIssues:handleSave] Issue Ref:', 'customers', activeTenancy.id, 'product', 'managementSystemGuide', 'issueRegister', 'register');
      // Ensure we're saving all required fields
      const dbData = {
        [issueToSave.id]: {
          ...issueToSave,
          details: {
            issueType: issueToSave.details?.issueType || '',
            risks: issueToSave.details?.risks || [],
            objectives: issueToSave.details?.objectives || [],
            legalObligations: issueToSave.details?.legalObligations || [],
            frameworks: issueToSave.details?.frameworks || []
          },
          // Ensure we have a date
          dateAdded: issueToSave.dateAdded || new Date().toISOString().split('T')[0]
        }
      };

      console.log('Saving to database:', dbData);
      await issueRef.set(dbData, { merge: true });

      // Increment version with snapshot
      try {
        await incrementVersion(
          isNew ? 'issue_create' : 'issue_update',
          isNew ? `Created issue: ${issueToSave.title}` : `Updated issue: ${issueToSave.title}`,
          {
            issueId: issueToSave.id,
            oldData: oldIssue,
            newData: issueToSave
          }
        );
      } catch (versionError) {
        console.error('Error updating version:', versionError);
      }

    } catch (error) {
      console.error('Error saving issue:', error);
      // TODO: Show error to user
    } finally {
      setDialogOpen(false);
      setSelectedEditIssue(null);
    }
  };

  const handleAddNew = () => {
    setSelectedEditIssue(null);
    setDialogOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="bg-card rounded-xl border border-border shadow-sm px-6 py-6 relative">
        <div className="absolute right-4 top-4 z-10">
          <div className="flex items-center space-x-2">
            <SupportingDocsDropdown pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
            <RelatedClausesAccordianItem modulesList={modulesList} clauseStandards={clauseStandards} setModulesList={setModulesList} />
          </div>
        </div>
        <div className="px-6 py-6">
          <MarkdownRenderer source={pageTemplateMDX} components={useMarkdownComponents(modulesList, clauseStandards, { current: pageClauses }, updatePageClauses)} />
        </div>
        <div style={{ maxWidth: '56rem', marginLeft: 'auto', marginRight: 'auto', padding: '0px 1rem 1.5rem', transition: 'color 0.2s, background-color 0.2s', backgroundColor: 'transparent' }}>
          <AdditionalContent pageRoute={pageRoute} />
          <FootNote />
        </div>
      </div>
      <div className='bg-card rounded-xl border border-border shadow-sm px-6 py-6'>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-medium">Internal and External Issues</h3>
          {!isReadOnly && (
            <Button onClick={handleAddNew} className="flex items-center space-x-1">
              <Plus className="h-4 w-4" />
              <span>Add Issue</span>
            </Button>
          )}
        </div>
        <div className="w-full overflow-x-auto -mx-4 px-4">
          <Table className="min-w-[600px] w-full table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px] sm:w-[100px] px-2 sm:px-4">Type</TableHead>
                <TableHead className="min-w-[120px] sm:min-w-[150px] px-2 sm:px-4">Name</TableHead>
                <TableHead className="min-w-[200px] sm:min-w-[300px] px-2 sm:px-4">Description</TableHead>
                <TableHead className="w-[100px] sm:w-[120px] text-right px-2 sm:px-4">Manage</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {[...issueRegisterItems].sort(byDisplayName).map(row => (
                <React.Fragment key={row.id}>
                  <TableRow onClick={() => toggleRow(row.id)}>
                    <TableCell>{row.type.charAt(0).toUpperCase() + row.type.slice(1)}</TableCell>
                    <TableCell>{row.name}</TableCell>
                    <TableCell className="whitespace-normal break-words max-w-[300px] sm:max-w-[400px] px-2 sm:px-4">
                      <div className="line-clamp-2">
                        {row.description}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={isReadOnly ? "View" : "Edit"}
                        onClick={e => { e.stopPropagation(); onActions.onEdit(row); }}
                      >
                        {isReadOnly ? <Eye className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
                      </Button>
                      <Button
                        onClick={e => { e.stopPropagation(); toggleRow(row.id); }}
                        variant="ghost"
                      >
                        {expandedRows.includes(row.id) ? '▼' : '▶'}
                      </Button>
                      {!isReadOnly && (
                        <Button
                          variant="ghost"
                          size="icon"
                          aria-label="Delete"
                          onClick={e => {
                            e.stopPropagation();
                            setDeleteIssueId(row.id);
                            setDeleteIssueOrigin(row.origin);
                            setDeleteConfirmOpen(true);
                          }}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                  {expandedRows.includes(row.id) && (
                    <TableRow>
                      <TableCell colSpan={4} className="bg-transparent text-muted-foreground p-4">
                        <div className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <div className="font-semibold text-sm mb-2">Issue Type</div>
                              <div className="text-sm">{row.details.issueType.charAt(0).toUpperCase() + row.details.issueType.slice(1)}</div>
                            </div>
                            <div>
                              <div className="font-semibold text-sm mb-2">Risks</div>
                              <div className="text-sm whitespace-pre-wrap">{renderArray(row.details.risks, 'id', 'RSK')}</div>
                            </div>
                            <div>
                              <div className="font-semibold text-sm mb-2">Objectives</div>
                              <div className="text-sm whitespace-pre-wrap">{renderArray(row.details.objectives, 'id', 'OBJ')}</div>
                            </div>
                            <div>
                              <div className="font-semibold text-sm mb-2">Legal Obligations</div>
                              <div className="text-sm whitespace-pre-wrap">{renderArray(row.details.legalObligations, 'id', 'LEO')}</div>
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-sm mb-2">Frameworks</div>
                            <div className="flex flex-wrap gap-2">
                              {row.details.frameworks?.length > 0
                                ? row.details.frameworks.map(f => {
                                  return <Badge key={f.standard || f} variant="secondary" className="text-xs">{cleanStandardName(f.standard || f)}</Badge>;
                                })
                                : <span className="text-sm">—</span>}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
      {/* <div className='bg-card rounded-xl border border-border shadow-sm overflow-hidden'>
        <SupportingDocs pageRoute={pageRoute} clauseStandards={clauseStandards} pageClauses={pageClauses} />
      </div> */}
      <InternalExternalIssuesDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setSelectedEditIssue(null);
          }
        }}
        issue={selectedEditIssue}
        onSave={handleSave}
        isReadOnly={isReadOnly}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Issue</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this issue? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setDeleteIssueId(null);
              setDeleteIssueOrigin(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleteIssueId) {
                  const companyID = activeTenancy.id;
                  await import('@/app/pages/msg/utils/common').then(async mod => {
                    await mod.deleteIssueRegisterItem(db, companyID, deleteIssueId, deleteIssueOrigin);
                  });
                  setIssueRegisterItems(prev => prev.filter(p => p.id !== deleteIssueId));
                }
                setDeleteConfirmOpen(false);
                setDeleteIssueId(null);
                setDeleteIssueOrigin(null);
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}



